import { BaseView, PromiseCompletionSource } from "scene-manager";
import { CommonUIResourcesPackage } from "../../common/resources/CommonUIResourcesPackage";
import { ICanvasScaler } from "../../../../canvas/ICanvasScaler";
import { NineSlicePlane } from "@pixi/mesh-extras";
import { IFocusable } from "../../../layers/IFocusable";
import { IDestroyOptions } from "@pixi/display";
import { SettingsResourcesPackage } from "../resources/SettingsResourcesPackage";
import { SettingsController } from "../controllers/SettingsController";
import { TextStyle, TextStyleAlign } from "@pixi/text";
import { ScaleUpButton } from "../../common/components/buttons/ScaleUpButton";
import { getDefaultFontFamily } from "../../common/TextUtils";
import { ToggleButton } from "../components/ToggleButton";
import { LanguagePanel } from "../components/LanguagePanel";
import { RetryButton } from "../components/RetryButton";
import { Localization } from "../../common/Localization";
import { LocalizedText } from "../../common/components/LocalizedText";
import { AudioManager } from "../../../audio/AudioManager";

export class SettingsView extends BaseView<void> implements IFocusable {

    public readonly focusable: boolean = true;

    private root: NineSlicePlane;
    private settingsTitleText: LocalizedText;
    private closeButton: ScaleUpButton;
    private soundsText: LocalizedText;
    private soundsToggleButton: ToggleButton;
    private musicText: LocalizedText;
    private musicToggleButton: ToggleButton;
    private languagePanel: LanguagePanel;
    private retryButton: RetryButton;

    public constructor(
        private readonly canvasScaler: ICanvasScaler,
        private readonly commonResources: CommonUIResourcesPackage,
        private readonly settingsResources: SettingsResourcesPackage,
        private readonly settingsController: SettingsController,
        private readonly localization: Localization,
        private readonly audioManager: AudioManager,
        operationControls: PromiseCompletionSource<void>
    ) {
        super(operationControls);
        this.eventMode = `static`;
        this.build();
        this.layout();
    }

    public override start(): void {
    }

    private build(): void {
        this.root = new NineSlicePlane(this.commonResources.assets_common_root_png, 105, 150, 105, 150);
        this.root.width = 800;
        this.root.height = 1060;
        this.addChild(this.root);
        this.settingsTitleText = new LocalizedText("settings_title", this.localization, this.getSettingsTextStyle(55), 380, 130);
        this.settingsTitleText.anchor.set(0.5);
        this.root.addChild(this.settingsTitleText);
        this.closeButton = new ScaleUpButton({ defaultTexture: this.commonResources.assets_common_close_button_png }, this.audioManager);
        this.root.addChild(this.closeButton);
        this.soundsText = new LocalizedText("sounds", this.localization, this.soundTextStyle, 260, 100);
        this.soundsText.anchor.set(0.5);
        this.root.addChild(this.soundsText);
        this.soundsToggleButton = new ToggleButton(
            this.settingsResources.assets_settings_toggle_on_png,
            this.settingsResources.assets_settings_toggle_off_png,
            this.audioManager.isPlaySounds,
            this.audioManager
        );
        this.root.addChild(this.soundsToggleButton);
        this.musicText = new LocalizedText("music", this.localization, this.soundTextStyle, 260, 100);
        this.musicText.anchor.set(0.5);
        this.root.addChild(this.musicText);
        this.musicToggleButton = new ToggleButton(
            this.settingsResources.assets_settings_toggle_on_png,
            this.settingsResources.assets_settings_toggle_off_png,
            this.audioManager.isPlayMusic,
            this.audioManager
        );
        this.root.addChild(this.musicToggleButton);
        this.languagePanel = new LanguagePanel(this.settingsResources, this.settingsController, this.localization.locale, this.audioManager);
        this.root.addChild(this.languagePanel);
        this.retryButton = new RetryButton({ defaultTexture: this.settingsResources.assets_settings_button_yellow_png }, this.localization, this.audioManager);
        this.root.addChild(this.retryButton);
        this.closeButton.onUp.add(this.handleCloseButtonClick, this);
        this.soundsToggleButton.onChange.add(this.handleSoundsToggleButtonChange, this);
        this.musicToggleButton.onChange.add(this.handleMusicToggleButtonChange, this);
        this.retryButton.onUp.add(this.handleRetryButtonClick, this);
    }

    private layout(): void {
        const referenceWidth = this.canvasScaler.referenceWidth;
        this.root.position.set((referenceWidth - this.root.width) * 0.5, 251);
        this.languagePanel.position.set((this.root.width - this.languagePanel.width) * 0.5, 170);
        this.settingsTitleText.position.set(this.root.width * 0.5, 60);
        this.closeButton.position.set(720, 40);
        this.soundsText.position.set(200, 600);
        this.soundsToggleButton.position.set(540, 580);
        this.musicText.position.set(200, 700);
        this.musicToggleButton.position.set(540, 680);
        this.retryButton.position.set((this.root.width - this.retryButton.width) * 0.5, 770);
    }

    private handleCloseButtonClick(): void {
        this.operationControls.complete();
    }

    private handleSoundsToggleButtonChange(isOn: boolean): void {
        this.settingsController.switchSounds(isOn);
    }

    private handleMusicToggleButtonChange(isOn: boolean): void {
        this.settingsController.switchMusic(isOn);
    }

    private async handleRetryButtonClick(): Promise<void> {
        const result = await this.settingsController.retryGame();
        if (result) {
            this.operationControls.complete();
        }
    }

    private getSettingsTextStyle(size: number, align: TextStyleAlign = 'center'): TextStyle {
        return new TextStyle({
            fontFamily: getDefaultFontFamily(),
            fontSize: size,
            fill: "#ffffff",
            stroke: "#006A5D",
            strokeThickness: 6,
            lineJoin: "round",
            align: align
        });
    }

    private get soundTextStyle(): TextStyle {
        return new TextStyle({
            fontFamily: getDefaultFontFamily(),
            fontSize: 36,
            fill: "#4A5F66",
            lineJoin: "round",
            align: "center"
        });
    }

    public override destroy(options?: IDestroyOptions | boolean): void {
        this.closeButton.onUp.remove(this.handleCloseButtonClick, this);
        this.soundsToggleButton.onChange.remove(this.handleSoundsToggleButtonChange, this);
        this.musicToggleButton.onChange.remove(this.handleMusicToggleButtonChange, this);
        this.retryButton.onUp.remove(this.handleRetryButtonClick, this);
        super.destroy(options);
    }
}